<template>
  <div class="customer-sales-history">
    <div class="sales-header">
      <h2>Historique des ventes</h2>

      <!-- Barre de recherche -->
      <div class="sales-search">
        <div class="search-input-wrapper">
          <input type="text" placeholder="Rechercher par numéro de facture..." v-model="salesSearch"
            @input="handleSalesSearch" />
          <BaseButton v-if="salesSearch" @click="clearSalesSearch" class="clear-search-btn">
            <i class="fa-solid fa-xmark"></i>
          </BaseButton>
        </div>
      </div>

      <div class="flex justify-between items-center gap-2">
        <!-- Filtres de statut -->
        <div class="sales-filters">
          <BaseButton :class="['filter-btn', { active: salesFilter === 'all' }]" @click="setSalesFilter('all')">
            Toutes
          </BaseButton>
          <BaseButton :class="['filter-btn', { active: salesFilter === 'pending' }]" @click="setSalesFilter('pending')">
            En attente
          </BaseButton>
          <BaseButton :class="['filter-btn', { active: salesFilter === 'completed' }]" @click="setSalesFilter('completed')">
            Complétées
          </BaseButton>
          <BaseButton :class="['filter-btn', { active: salesFilter === 'paid' }]" @click="setSalesFilter('paid')">
            Payées
          </BaseButton>
          <BaseButton :class="['filter-btn', { active: salesFilter === 'refunded' }]" @click="setSalesFilter('refunded')">
            Remboursées
          </BaseButton>
        </div>

        <!-- Filtres de date -->
        <div class="sales-date-filters">
          <div class="date-filter-group">
            <label>Du</label>
            <input type="date" v-model="dateFrom" @change="handleDateFilterChange" />
          </div>
          <div class="date-filter-group">
            <label>Au</label>
            <input type="date" v-model="dateTo" @change="handleDateFilterChange" />
          </div>
          <BaseButton v-if="dateFrom || dateTo" @click="clearDateFilters" class="clear-date-btn">
            <i class="fa-solid fa-xmark"></i>
            Réinitialiser
          </BaseButton>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="salesLoading" class="sales-loading">
      <div class="spinner-small"></div>
      <p>Chargement...</p>
    </div>

    <!-- Empty -->
    <div v-else-if="sales.length === 0" class="no-sales">
      <i class="fa-solid fa-file-invoice"></i>
      <h3>Aucune vente {{ getSalesFilterLabel() }}</h3>
    </div>

    <!-- Barre d'action pour les ventes sélectionnées -->
    <div v-if="selectedSales.length > 0" class="sales-selection-bar">
      <div class="selection-info">
        <span class="selection-count">{{ selectedSales.length }} vente(s) sélectionnée(s)</span>
      </div>
      <div class="selection-actions">
        <BaseButton @click="clearSelection" class="clear-selection-btn">
          Annuler
        </BaseButton>
        <BaseButton v-if="selectedSales.length >= 2" @click="createInvoice" :disabled="isCreatingInvoice"
          class="create-invoice-btn">
          <span v-if="isCreatingInvoice" class="spinner-small"></span>
          <span v-else>
            <i class="fa-solid fa-file-invoice"></i>
            Créer une facture
          </span>
        </BaseButton>
      </div>
    </div>

    <!-- Tableau -->
    <div v-if="sales.length > 0" class="sales-table-container">
      <table class="sales-table">
        <thead>
          <tr>
            <th class="th-checkbox"></th>
            <th>N° Facture</th>
            <th>Date</th>
            <th>Solde</th>
            <th>Montant</th>
            <th>Statut</th>
            <th>Facture</th>
            <th class="th-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="sale in sales" :key="sale.id" :class="{ 'selected': isSaleSelected(sale.id) }">
            <td class="td-checkbox">
              <div v-if="isUninvoicedSale(sale)" class="sale-checkbox" @click.stop="toggleSaleSelection(sale.id)">
                <input type="checkbox" :checked="isSaleSelected(sale.id)" @change="toggleSaleSelection(sale.id)"
                  @click.stop class="checkbox-input" />
              </div>
            </td>
            <td class="td-invoice">
              <span class="invoice-number" v-if="!sale.sale_invoice_id" @click="viewSale(sale)">{{ sale.invoice_number }}</span>
            </td>
            <td class="td-date">{{ formatDate(sale.sale_date) }}</td>
            <td class="td-amount">{{ formatCurrency(sale.remaining_amount) }}</td>
            <td class="td-amount">{{ formatCurrency(sale.total_incl_tax) }}</td>
            <td class="td-status">
              <SaleStatusBadge :sale="sale" />
            </td>
            <td class="td-invoice">
              <span class="invoice-number" v-if="sale.sale_invoice_id" @click="viewSaleInvoice(sale)">{{ sale.sale_invoice?.invoice_number }}</span>
              <span v-else>N/A</span>
            </td>
            <td class="td-actions">
              <div class="action-buttons">
                <BaseButton v-if="isUnpaidSale(sale)  && !sale.sale_invoice_id" @click.stop="paySingleSale(sale)" class="pay-btn" title="Encaisser">
                  <i class="fa-solid fa-money-bill"></i>
                </BaseButton>
                <BaseButton @click.stop="viewSale(sale)" class="view-btn" title="Voir">
                  <i class="fa-solid fa-eye"></i>
                </BaseButton>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <PaginationComponent v-if="!salesLoading && sales.length > 0" :current-page="currentPage" :per-page="perPage"
      :total="total" :to="paginationTo" :from="paginationFrom" @page-change="goToPage" />

    <!-- Modal paiement -->
    <PaymentModal :show="showPaymentModal" :sale="selectedSaleForPayment" :customer="customer" :wallet="wallet" @close="closePaymentModal"
      @payment-success="onPaymentSuccess" />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import customerService from '@/services/customerService';
import { saleInvoiceService } from '@/services';
import PaymentModal from '@/views/sales/PaymentModal.vue';
import PaginationComponent from '@/components/common/PaginationComponent.vue';
import SaleStatusBadge from '@/components/sales/SaleStatusBadge.vue';

export default defineComponent({
  name: 'CustomerSalesHistory',
  components: {
    PaymentModal,
    PaginationComponent,
    SaleStatusBadge
  },
  props: {
    customerId: {
      type: [Number, String],
      required: true
    },
    wallet: {
      type: Object,
      required: false
    },
    customer: {
      type: Object,
      required: false
    }
  },
  emits: ['refresh'],
  setup(props, { emit }) {
    const router = useRouter();

    const sales = ref([]);
    const salesLoading = ref(false);
    const salesFilter = ref('all');
    const salesSearch = ref('');
    const dateFrom = ref('');
    const dateTo = ref('');
    const searchTimeout = ref(null);
    const selectedSaleForPayment = ref({});
    const showPaymentModal = ref(false);

    // Pagination
    const currentPage = ref(1);
    const perPage = ref(10);
    const total = ref(0);
    const paginationFrom = ref(0);
    const paginationTo = ref(0);

    console.log('props', props);
    console.log('props.wallet', props.wallet);

    // Une vente est considérée comme impayée si elle n'a pas de paiements
    const isUnpaidSale = (sale) => {
      return !sale.payments || sale.payments.length === 0 && sale.status !== 'refunded';
    };

    // Vérifier si une vente est complétée et non facturée
    const isUninvoicedSale = (sale) => {
      return sale.status === 'completed'
        && (sale.sale_invoice_id === null || sale.sale_invoice_id === undefined);
    };

    // Gestion de la sélection des ventes pour facturation
    const selectedSales = ref([]);

    const toggleSaleSelection = (saleId) => {
      const index = selectedSales.value.indexOf(saleId);
      if (index > -1) {
        selectedSales.value.splice(index, 1);
      } else {
        selectedSales.value.push(saleId);
      }
    };

    const isSaleSelected = (saleId) => {
      return selectedSales.value.includes(saleId);
    };

    const clearSelection = () => {
      selectedSales.value = [];
    };

    // État pour la création de facture
    const isCreatingInvoice = ref(false);

    // Créer une facture à partir des ventes sélectionnées
    const createInvoice = async () => {
      if (selectedSales.value.length < 2) {
        return;
      }

      if (!props.customerId) {
        console.error('Client non trouvé');
        return;
      }

      try {
        isCreatingInvoice.value = true;

        const invoiceData = {
          customer_id: props.customerId,
          sale_ids: selectedSales.value
        };

        await saleInvoiceService.createSaleInvoice(invoiceData);

        // Réinitialiser la sélection
        clearSelection();

        // Recharger les données
        await loadCustomerSales();
        emit('refresh');

      } catch (error) {
        console.error('Erreur lors de la création de la facture:', error);
      } finally {
        isCreatingInvoice.value = false;
      }
    };

    const loadCustomerSales = async () => {
      if (!props.customerId) return;

      try {
        salesLoading.value = true;

        // Préparer les paramètres de pagination
        const params = {
          page: currentPage.value,
          per_page: perPage.value,
          with: ['saleInvoice']
        };

        // Ajouter le filtre de recherche
        if (salesSearch.value && salesSearch.value.trim()) {
          params.search = salesSearch.value.trim();
        }

        // Ajouter le filtre de statut
        if (salesFilter.value !== 'all') {
          params.status = salesFilter.value;
        }

        // Ajouter les filtres de date
        if (dateFrom.value) {
          params.date_from = dateFrom.value;
        }
        if (dateTo.value) {
          params.date_to = dateTo.value;
        }

        const response = await customerService.getCustomerSales(props.customerId, params);

        // Gérer la réponse avec data et meta
        if (response.data.data) {
          sales.value = response.data.data || [];
        } else {
          sales.value = [];
        }

        // Mettre à jour les données de pagination depuis meta
        if (response.data.meta) {
          const meta = response.data.meta;
          total.value = meta.total || 0;
          perPage.value = meta.per_page || 10;
          paginationFrom.value = meta.from || 0;
          paginationTo.value = meta.to || 0;
        }
      } catch (error) {
        console.error('Erreur:', error);
        sales.value = [];
      } finally {
        salesLoading.value = false;
      }
    };

    const setSalesFilter = (filter) => {
      salesFilter.value = filter;
      currentPage.value = 1;
      loadCustomerSales();
    };

    const handleSalesSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        loadCustomerSales();
      }, 500);
    };

    const clearSalesSearch = () => {
      salesSearch.value = '';
      currentPage.value = 1;
      loadCustomerSales();
    };

    const handleDateFilterChange = () => {
      currentPage.value = 1;
      loadCustomerSales();
    };

    const clearDateFilters = () => {
      dateFrom.value = '';
      dateTo.value = '';
      currentPage.value = 1;
      loadCustomerSales();
    };

    const goToPage = (page) => {
      if (page < 1) return;
      currentPage.value = page;
      loadCustomerSales();
    };

    const getSalesFilterLabel = () => {
      const labels = {
        'all': '',
        'pending': 'en attente',
        'completed': 'complétée',
        'paid': 'payée',
        'refunded': 'remboursée'
      };
      return labels[salesFilter.value] || '';
    };

    const viewSale = (sale) => {
      router.push({ name: 'sale-details', params: { id: sale.id } });
    };

    const viewSaleInvoice = (sale) => {
      router.push({ name: 'sale-invoice-details', params: { id: sale.sale_invoice.id } });
    };

    const paySingleSale = (sale) => {
      selectedSaleForPayment.value = sale;
      showPaymentModal.value = true;
    };

    const closePaymentModal = () => {
      showPaymentModal.value = false;
      selectedSaleForPayment.value = {};
    };

    const onPaymentSuccess = async () => {
      closePaymentModal();
      await loadCustomerSales();
      emit('refresh');
    };

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0);
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    // Expose reload method
    const reload = () => {
      loadCustomerSales();
    };

    watch(() => props.customerId, () => {
      currentPage.value = 1;
      loadCustomerSales();
    });

    onMounted(() => {
      loadCustomerSales();
    });

    return {
      sales,
      salesLoading,
      salesFilter,
      salesSearch,
      dateFrom,
      dateTo,
      selectedSaleForPayment,
      showPaymentModal,
      isUnpaidSale,
      isUninvoicedSale,
      selectedSales,
      toggleSaleSelection,
      isSaleSelected,
      clearSelection,
      isCreatingInvoice,
      createInvoice,
      setSalesFilter,
      getSalesFilterLabel,
      handleSalesSearch,
      clearSalesSearch,
      handleDateFilterChange,
      clearDateFilters,
      paySingleSale,
      closePaymentModal,
      onPaymentSuccess,
      viewSale,
      viewSaleInvoice,
      formatCurrency,
      formatDate,
      // Pagination
      currentPage,
      perPage,
      total,
      paginationFrom,
      paginationTo,
      goToPage,
      reload
    };
  }
});
</script>

<style scoped>
.customer-sales-history {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.sales-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #f3f4f6;
}

.sales-header h2 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.sales-search {
  width: 100%;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}


.search-input-wrapper input {
  width: 100%;
  padding: 0.625rem 2.5rem 0.625rem 2.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  transition: all 0.2s;
}

.search-input-wrapper input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.clear-search-btn:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.sales-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-btn:hover {
  background: #f9fafb;
}

.filter-btn.active {
  background: #0A5C6B;
  color: white;
  border-color: #0A5C6B;
}

.sales-date-filters {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.date-filter-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.date-filter-group label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

.date-filter-group input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #111827;
  transition: all 0.2s;
}

.date-filter-group input:focus {
  outline: none;
  border-color: #0A5C6B;
  box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.clear-date-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-date-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #111827;
}

.sales-loading,
.no-sales {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  color: #6b7280;
}

.sales-selection-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f0fdfa;
  border-bottom: 2px solid #0A5C6B;
  margin-bottom: 0;
}

.selection-info {
  display: flex;
  align-items: center;
}

.selection-count {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0A5C6B;
}

.selection-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.clear-selection-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-selection-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #111827;
}

.create-invoice-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  background: #0A5C6B;
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.create-invoice-btn:hover:not(:disabled) {
  background: #094854;
}

.create-invoice-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}


/* Table styles */
.sales-table-container {
  overflow-x: auto;
}

.sales-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.sales-table thead {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.sales-table th {
  padding: 0.875rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #475569;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.sales-table th.th-checkbox {
  width: 40px;
  padding-left: 1.5rem;
}

.sales-table th.th-actions {
  width: 100px;
  text-align: center;
}

.sales-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s;
}

.sales-table tbody tr:hover {
  background: #f8fafc;
}

.sales-table tbody tr.selected {
  background: #f0fdfa;
}

.sales-table td {
  padding: 1rem;
  color: #334155;
  vertical-align: middle;
}

.sales-table td.td-checkbox {
  padding-left: 1.5rem;
  width: 40px;
}

.sale-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.sale-checkbox .checkbox-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #0A5C6B;
}

.td-invoice .invoice-number {
  font-weight: 600;
  color: #0A5C6B;
  cursor: pointer;
  transition: color 0.15s;
}

.td-invoice .invoice-number:hover {
  color: #094854;
  text-decoration: underline;
}

.td-date {
  color: #64748b;
  white-space: nowrap;
}

.td-amount {
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
}

.td-status {
  white-space: nowrap;
}

.td-actions {
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.pay-btn,
.view-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.pay-btn {
  color: #059669;
  border-color: #d1fae5;
}

.pay-btn:hover {
  background: #ecfdf5;
  border-color: #a7f3d0;
}

.view-btn {
  color: #6b7280;
  border-color: #e5e7eb;
}

.view-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .sales-header {
    flex-direction: column;
    gap: 1rem;
  }

  .sales-table th,
  .sales-table td {
    padding: 0.75rem 0.5rem;
  }

  .sales-table th.th-checkbox,
  .sales-table td.td-checkbox {
    padding-left: 0.75rem;
  }

  .td-invoice .invoice-number {
    font-size: 0.8rem;
  }
}
</style>
